/**
 * MarketingPage — generic public renderer for admin-managed marketing pages.
 *
 * Reads :slug from the URL, fetches the published layout via usePageLayout,
 * and renders sections via sectionRegistry — the exact same pipeline as
 * JoinUsAsLibrary, but generic (any slug) and with server-resolved
 * featuredLibraries support.
 *
 * Route: /p/:slug
 *
 * For featuredLibraries sections:
 *   - If the server response includes featuredLibraries[section.id], those
 *     are used (admin-chosen, ordered).
 *   - Otherwise falls back to the dynamic top-libraries list from useLibraries.
 *
 * On 404 / unpublished / missing: renders a clean not-found state.
 */

import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Typography, Button, Skeleton, Container } from '@mui/material';
import { motion } from 'framer-motion';
import usePageLayout from '../marketing/hooks/usePageLayout';
import useLibraries from '../marketing/hooks/useLibraries';
import sectionRegistry from '../marketing/editable/sectionRegistry';

// ─── Dynamic props injection (mirrors JoinUsAsLibrary) ────────────────────────

/**
 * Injects live-data props for sections that require them.
 * For featuredLibraries: if the server has resolved pinned libs for this
 * section (featuredLibraries[section.id]), those take precedence over the
 * dynamic top-libraries. Otherwise the dynamic list is used as usual.
 */
function dynamicPropsFor(section, liveData, serverFeaturedLibraries) {
  const { totalCount, uniqueCities, topLibraries, websiteLibraries, loading } = liveData;

  switch (section.type) {
    case 'hero':
      return { totalCount };

    case 'socialProof':
      return { totalCount, uniqueCities, loading };

    case 'websiteAddon':
      return { libraries: websiteLibraries, loading };

    case 'featuredLibraries': {
      // Server-resolved pinned libraries take priority when present
      const pinned = serverFeaturedLibraries[section.id];
      if (Array.isArray(pinned) && pinned.length > 0) {
        return { libraries: pinned, loading: false };
      }
      return { libraries: topLibraries, loading };
    }

    case 'libraries': {
      // Generic libraries block — same resolution logic as featuredLibraries
      const pinned = serverFeaturedLibraries[section.id];
      if (Array.isArray(pinned) && pinned.length > 0) {
        return { libraries: pinned, loading: false };
      }
      return { libraries: topLibraries, loading };
    }

    default:
      return {};
  }
}

// ─── Loading skeleton ─────────────────────────────────────────────────────────

function PageSkeleton() {
  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', pt: 2 }}>
      {/* Hero-like header skeleton */}
      <Box sx={{ px: { xs: 3, md: 8 }, pt: 8, pb: 6 }}>
        <Skeleton variant="text" width="30%" height={24} sx={{ mb: 2 }} />
        <Skeleton variant="text" width="65%" height={56} sx={{ mb: 1 }} />
        <Skeleton variant="text" width="50%" height={56} sx={{ mb: 3 }} />
        <Skeleton variant="text" width="80%" height={20} sx={{ mb: 1 }} />
        <Skeleton variant="text" width="70%" height={20} sx={{ mb: 4 }} />
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Skeleton variant="rounded" width={160} height={48} />
          <Skeleton variant="rounded" width={140} height={48} />
        </Box>
      </Box>
      {/* Body section skeleton */}
      <Box sx={{ px: { xs: 3, md: 8 }, pb: 8 }}>
        <Skeleton variant="rounded" width="100%" height={240} sx={{ borderRadius: 4 }} />
      </Box>
    </Box>
  );
}

// ─── Not-found state ──────────────────────────────────────────────────────────

function PageNotFound({ slug }) {
  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      sx={{
        bgcolor: 'background.default',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Container maxWidth="sm" sx={{ textAlign: 'center', py: 12 }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { xs: '4rem', md: '6rem' },
            fontWeight: 800,
            lineHeight: 1,
            color: 'text.disabled',
            mb: 2,
          }}
        >
          404
        </Typography>
        <Typography
          variant="h5"
          fontWeight={700}
          gutterBottom
          sx={{ color: 'text.primary' }}
        >
          Page not found
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ mb: 4, lineHeight: 1.6 }}
        >
          The page{' '}
          <Box component="code" sx={{ fontFamily: 'monospace', fontSize: '0.9em', color: 'text.primary' }}>
            /p/{slug}
          </Box>{' '}
          does not exist or has not been published yet.
        </Typography>
        <Button
          variant="contained"
          href="/"
          sx={{
            borderRadius: 3,
            px: 4,
            py: 1.5,
            fontWeight: 700,
            textTransform: 'none',
            fontSize: '1rem',
          }}
        >
          Go to homepage
        </Button>
      </Container>
    </Box>
  );
}

// ─── MarketingPage (default export) ──────────────────────────────────────────

export default function MarketingPage() {
  const { slug } = useParams();

  // Live library data for dynamic sections
  const { totalCount, uniqueCities, topLibraries, websiteLibraries, loading: libsLoading } = useLibraries();

  // Published layout + server-resolved featured libraries
  const { layout, featuredLibraries, loading: layoutLoading } = usePageLayout(slug);

  // Update document title when layout loads
  useEffect(() => {
    if (!layoutLoading && layout.length > 0) {
      // Try to derive a meaningful title from the page's hero section.
      // Check legacy hero (headingPrefix) and generic genericHero (heading).
      const legacyHero = layout.find(s => s.type === 'hero');
      const genericHero = layout.find(s => s.type === 'genericHero');

      let derivedTitle = null;
      if (legacyHero?.props?.headingPrefix) {
        derivedTitle = `${legacyHero.props.headingPrefix} — BookMyLibrary`;
      } else if (genericHero?.props?.heading) {
        derivedTitle = `${genericHero.props.heading} — BookMyLibrary`;
      } else {
        // Fall back to the first section that has any heading-like prop
        const firstWithHeading = layout.find(
          s => s.props?.heading || s.props?.title || s.props?.headingPrefix
        );
        if (firstWithHeading?.props) {
          const h =
            firstWithHeading.props.heading ||
            firstWithHeading.props.title ||
            firstWithHeading.props.headingPrefix;
          if (h) derivedTitle = `${h} — BookMyLibrary`;
        }
      }
      document.title = derivedTitle || 'BookMyLibrary';
    }
  }, [layout, layoutLoading]);

  // Show loading skeleton while layout is fetching
  if (layoutLoading) {
    return <PageSkeleton />;
  }

  // Empty layout = page not found or not published
  if (!layout || layout.length === 0) {
    return <PageNotFound slug={slug} />;
  }

  const liveData = {
    totalCount,
    uniqueCities,
    topLibraries,
    websiteLibraries,
    loading: libsLoading,
  };

  const visibleSections = layout.filter(s => s.visible !== false);

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      {visibleSections.map((section) => {
        const Cmp = sectionRegistry[section.type];
        if (!Cmp) return null;

        return (
          <Cmp
            key={section.id}
            {...(section.props || {})}
            {...dynamicPropsFor(section, liveData, featuredLibraries)}
          />
        );
      })}
    </Box>
  );
}
